import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { RefreshCw, ShieldCheck, FileDown, Mail, Search, ChevronDown, ChevronUp, X, Building2, Wand2 } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import { PageHeader } from '../components/FilterBar';

// ── mask decoding + per-snapshot metrics (spec §4) ───────────────────────────
const decodeMask = (hex) => BigInt('0x' + (hex && hex !== '0' ? hex : '0'));

function computeMetrics(data) {
  const M = data.modules.length;
  const catalog = data.modules.reduce((s, m) => s + m.labels.length, 0);
  const moduleStats = data.modules.map(m => ({ name: m.name, size: m.labels.length, users: 0, grants: 0, byType: new Array(8).fill(0), holders: [] }));
  const byTypeAll = new Array(8).fill(0);

  const users = data.users.map(u => {
    let total = 0;
    const byType = new Array(8).fill(0);
    const perMod = new Array(M).fill(0);
    const grantedBits = []; // per module: Set of granted label indices
    const adminMods = [], sodMods = [];
    let delCount = 0;
    u.masks.forEach((hex, mi) => {
      let b = decodeMask(hex);
      const bits = new Set();
      const mod = data.modules[mi];
      let addEdit = false, appr = false, adminHere = false;
      for (let i = 0; b; i++, b >>= 1n) {
        if (!(b & 1n)) continue;
        bits.add(i);
        const t = mod.types[i];
        total++; byType[t]++; perMod[mi]++; byTypeAll[t]++;
        moduleStats[mi].grants++; moduleStats[mi].byType[t]++;
        if (t === 1 || t === 2) addEdit = true;
        if (t === 4) appr = true;
        if (t === 3) delCount++;
        if (mod.labels[i].toUpperCase().includes('ADMIN')) adminHere = true;
      }
      grantedBits.push(bits);
      if (bits.size > 0) { moduleStats[mi].users++; moduleStats[mi].holders.push({ id: u.id, granted: bits.size }); }
      if (adminHere) adminMods.push(mod.name);
      if (addEdit && appr) sodMods.push(mod.name);
    });
    const coverage = perMod.filter(c => c > 0).length;
    return { id: u.id, name: u.name, printed: u.printed, total, byType, perMod, grantedBits, coverage, adminMods, sodMods, delCount };
  });

  const granted = users.reduce((s, u) => s + u.total, 0);
  return {
    users, modules: moduleStats, catalog, granted,
    byTypeAll,
    pct: catalog * users.length ? granted / (catalog * users.length) * 100 : 0,
    avg: users.length ? Math.round(granted / users.length) : 0,
    adminUsers: users.filter(u => u.adminMods.length).sort((a, b) => b.adminMods.length - a.adminMods.length),
    sodUsers: users.filter(u => u.sodMods.length).sort((a, b) => b.sodMods.length - a.sodMods.length),
    deleteUsers: users.filter(u => u.delCount).sort((a, b) => b.delCount - a.delCount),
    broadUsers: users.filter(u => u.coverage >= 0.8 * M).sort((a, b) => b.coverage - a.coverage),
    zeroUsers: users.filter(u => u.total === 0),
  };
}

// ── horizontal bar chart (div-based) ─────────────────────────────────────────
function HBar({ items, color = '#3b82f6', onClick, maxRows = 999 }) {
  const max = Math.max(...items.map(i => i.value), 1);
  return (
    <div className="space-y-1">
      {items.slice(0, maxRows).map((it, i) => (
        <div key={i} onClick={onClick ? () => onClick(it) : undefined}
          className="flex items-center gap-2 group" style={{ cursor: onClick ? 'pointer' : 'default' }}
          title={it.tooltip || undefined}>
          <div className="text-xs text-right flex-shrink-0" style={{ width: 150, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.label}</div>
          <div className="flex-1 flex items-center gap-2" style={{ borderLeft: '1px solid rgba(255,255,255,0.12)' }}>
            <div className="rounded-r group-hover:brightness-125" style={{ width: `${Math.max(it.value / max * 100, 0.6)}%`, height: 14, background: color, transition: 'filter 0.12s' }} />
            <span className="text-xs font-medium" style={{ color: '#e2e8f0', whiteSpace: 'nowrap' }}>{it.value.toLocaleString()}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

const Card = ({ title, children, right }) => (
  <div className="glass glow-border rounded-2xl p-5">
    {(title || right) && (
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm font-semibold" style={{ color: '#e2e8f0' }}>{title}</div>
        {right}
      </div>
    )}
    {children}
  </div>
);

const RISK_DEFS = [
  { key: 'adminUsers', title: 'Admin authority', color: '#f87171', def: 'Users holding module-level ADMIN permissions — highest privilege.', fmt: u => `${u.id} (${u.adminMods.length})` },
  { key: 'sodUsers', title: 'Maker-checker conflicts', color: '#fb923c', def: 'Can both create/edit AND approve in the same module (SoD conflict).', fmt: u => `${u.id} (${u.sodMods.length})` },
  { key: 'deleteUsers', title: 'Delete / cancel rights', color: '#fbbf24', def: 'Hold delete, cancel or reversal permissions anywhere in the ERP.', fmt: u => `${u.id} (${u.delCount})` },
  { key: 'zeroUsers', title: 'Zero-access accounts', color: '#34d399', def: 'No permissions granted — review for removal.', fmt: u => u.id },
];

export default function ERPAccess() {
  const { user } = useAuth();
  const [snapshots, setSnapshots] = useState([]);
  const [mapModal, setMapModal] = useState(false);
  const [mapRows, setMapRows] = useState([]); const [mapCompanies, setMapCompanies] = useState([]);
  const [mapSnapshots, setMapSnapshots] = useState([]); const [mapKey, setMapKey] = useState('');
  const [mapTab, setMapTab] = useState('dates');
  const [mapLoading, setMapLoading] = useState(false); const [mapSearch, setMapSearch] = useState('');
  const [mapOnlyUnmapped, setMapOnlyUnmapped] = useState(false); const [matching, setMatching] = useState(false);
  const [diskFolders, setDiskFolders] = useState([]);
  const [selKey, setSelKey] = useState('');
  const [baseKey, setBaseKey] = useState('');
  const [data, setData] = useState(null);         // current snapshot contract
  const [baseData, setBaseData] = useState(null); // comparison baseline
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [error, setError] = useState('');
  const [moduleMetric, setModuleMetric] = useState('users');
  const [search, setSearch] = useState('');
  const [globalQ, setGlobalQ] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [sortCol, setSortCol] = useState('total');
  const [sortDir, setSortDir] = useState(-1);
  const [userModal, setUserModal] = useState(null);   // metrics user object
  const [showDenied, setShowDenied] = useState(false);
  const [moduleModal, setModuleModal] = useState(null); // module stats object
  const [emailModal, setEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState('');

  const loadSnapshots = useCallback(async (autoselect = true) => {
    try {
      const r = await api.get('/erp-access/snapshots');
      setSnapshots(r.data.snapshots || []);
      setDiskFolders(r.data.diskFolders || []);
      if (autoselect && (r.data.snapshots || []).length && !selKey) setSelKey(r.data.snapshots[0].key);
    } catch (e) { setError(e.response?.data?.error || 'Failed to load snapshots'); }
  }, [selKey]);

  useEffect(() => { loadSnapshots(); }, [loadSnapshots]);

  useEffect(() => {
    if (!selKey) { setData(null); return; }
    setLoading(true);
    api.get(`/erp-access/snapshots/${selKey}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || 'Failed to load snapshot'))
      .finally(() => setLoading(false));
  }, [selKey]);

  useEffect(() => {
    if (!baseKey || baseKey === selKey) { setBaseData(null); return; }
    api.get(`/erp-access/snapshots/${baseKey}`).then(r => setBaseData(r.data)).catch(() => setBaseData(null));
  }, [baseKey, selKey]);

  const loadMappings = async (key) => {
    setMapLoading(true);
    try {
      const r = await api.get('/erp-access/mappings', { params: key ? { key } : {} });
      setMapRows(r.data.accounts || []);
      setMapCompanies(r.data.companies || []);
      setMapSnapshots(r.data.snapshots || []);
      setMapKey(r.data.snapshot || '');
    } catch (e) { setError(e.response?.data?.error || 'Failed to load the company mapping'); }
    finally { setMapLoading(false); }
  };

  // One upload date → one company. This is the main control: every account in
  // that export inherits it, and until it is set nobody but admin sees the export.
  const setSnapshotCompany = async (key, companyId) => {
    setMapSnapshots(rows => rows.map(r => r.key === key
      ? { ...r, company_id: companyId || null, company_name: mapCompanies.find(c => c.id === companyId)?.name || null }
      : r));
    try {
      await api.put(`/erp-access/mappings/snapshot/${key}`, { company_id: companyId || null });
      await loadMappings(mapKey || key);
      loadSnapshots(false);
    } catch (e) { setError(e.response?.data?.error || 'Could not save'); loadMappings(mapKey); }
  };

  const setAccountCompany = async (accountId, companyId) => {
    try {
      await api.put(`/erp-access/mappings/${encodeURIComponent(accountId)}`, { company_id: companyId || null });
      await loadMappings(mapKey);
    } catch (e) { setError(e.response?.data?.error || 'Could not save'); loadMappings(mapKey); }
  };

  const runAutoMatch = async () => {
    setMatching(true);
    try {
      const r = await api.post('/erp-access/mappings/auto-match');
      await loadMappings(mapKey);
      if (selKey) api.get(`/erp-access/snapshots/${selKey}`).then(r => setData(r.data)).catch(() => {});
      setError('');
      window.alert(`Matched ${r.data.matched} of ${r.data.total} accounts by employee name.` +
        (r.data.ambiguous ? `\n${r.data.ambiguous} matched employees in more than one company — assign those by hand.` : '') +
        (r.data.unmatched ? `\n${r.data.unmatched} have no matching employee record — assign those by hand.` : ''));
    } catch (e) { setError(e.response?.data?.error || 'Auto-match failed'); }
    finally { setMatching(false); }
  };

  const handleGenerate = async () => {
    setGenerating(true); setError(''); setGenResult(null);
    try {
      const r = await api.post('/erp-access/generate', {});
      setGenResult(r.data);
      await loadSnapshots(false);
      if (r.data.parsed?.length) setSelKey(r.data.parsed[r.data.parsed.length - 1].key);
      else if (!selKey && r.data.skipped?.length) setSelKey(r.data.skipped[r.data.skipped.length - 1]);
    } catch (e) { setError(e.response?.data?.error || 'Generate failed'); }
    finally { setGenerating(false); }
  };

  const m = useMemo(() => data ? computeMetrics(data) : null, [data]);

  // ── comparison (§7) ──
  const compare = useMemo(() => {
    if (!data || !baseData) return null;
    const bUsers = new Map(baseData.users.map(u => [u.id, u]));
    const cUsers = new Map(data.users.map(u => [u.id, u]));
    const added = data.users.filter(u => !bUsers.has(u.id)).map(u => u.id);
    const removed = baseData.users.filter(u => !cUsers.has(u.id)).map(u => u.id);
    const bModIdx = new Map(baseData.modules.map((mo, i) => [mo.name, i]));
    const changes = [];
    for (const cu of data.users) {
      const bu = bUsers.get(cu.id);
      if (!bu) continue;
      let gained = 0, lost = 0;
      const mods = new Set();
      data.modules.forEach((mod, mi) => {
        const cSet = new Set();
        let b = decodeMask(cu.masks[mi]);
        for (let i = 0; b; i++, b >>= 1n) if (b & 1n) cSet.add(mod.labels[i]);
        const bmi = bModIdx.get(mod.name);
        const bSet = new Set();
        if (bmi !== undefined) {
          let bb = decodeMask(bu.masks[bmi]);
          const bMod = baseData.modules[bmi];
          for (let i = 0; bb; i++, bb >>= 1n) if (bb & 1n) bSet.add(bMod.labels[i]);
        }
        for (const l of cSet) if (!bSet.has(l)) { gained++; mods.add(mod.name); }
        for (const l of bSet) if (!cSet.has(l)) { lost++; mods.add(mod.name); }
      });
      // modules present only in baseline
      baseData.modules.forEach((bMod, bmi) => {
        if (data.modules.some(mo => mo.name === bMod.name)) return;
        let bb = decodeMask(bu.masks[bmi]);
        for (let i = 0; bb; i++, bb >>= 1n) if (bb & 1n) { lost++; mods.add(bMod.name); }
      });
      if (gained || lost) changes.push({ id: cu.id, gained, lost, mods: [...mods] });
    }
    changes.sort((a, b) => (b.gained + b.lost) - (a.gained + a.lost));
    return { added, removed, changes };
  }, [data, baseData]);

  // Global search — users, modules and individual permission labels
  const globalResults = useMemo(() => {
    if (!m || !data) return null;
    const q = globalQ.trim().toLowerCase();
    if (q.length < 2) return null;
    const users = m.users.filter(u => u.id.toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q)).slice(0, 8);
    const modules = m.modules.filter(mod => mod.name.toLowerCase().includes(q)).slice(0, 6);
    const perms = [];
    data.modules.forEach((mod, mi) => {
      mod.labels.forEach((label, li) => {
        if (perms.length >= 10) return;
        if (label.toLowerCase().includes(q)) {
          let holders = 0;
          for (const u of m.users) if (u.grantedBits[mi].has(li)) holders++;
          perms.push({ label, module: mod.name, holders, modStats: m.modules[mi] });
        }
      });
    });
    return { users, modules, perms, empty: !users.length && !modules.length && !perms.length };
  }, [m, data, globalQ]);

  const filteredUsers = useMemo(() => {
    if (!m) return [];
    const q = search.trim().toLowerCase();
    let rows = m.users;
    if (q) rows = rows.filter(u => u.id.toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q));
    if (riskFilter === 'admin') rows = rows.filter(u => u.adminMods.length > 0);
    else if (riskFilter === 'sod') rows = rows.filter(u => u.sodMods.length > 0);
    else if (riskFilter === 'delete') rows = rows.filter(u => u.delCount > 0);
    else if (riskFilter === 'approve') rows = rows.filter(u => u.byType[4] > 0);
    else if (riskFilter === 'zero') rows = rows.filter(u => u.total === 0);
    const get = { account: u => u.id, name: u => u.name || '', total: u => u.total, coverage: u => u.coverage, del: u => u.delCount, appr: u => u.byType[4], admin: u => u.adminMods.length, sod: u => u.sodMods.length };
    const g = get[sortCol] || get.total;
    return rows.slice().sort((a, b) => {
      const va = g(a), vb = g(b);
      return (typeof va === 'string' ? va.localeCompare(vb) : va - vb) * sortDir;
    });
  }, [m, search, riskFilter, sortCol, sortDir]);

  const maxUserTotal = useMemo(() => m ? Math.max(...m.users.map(x => x.total), 1) : 1, [m]);

  const setSort = (col) => {
    if (sortCol === col) setSortDir(d => -d);
    else { setSortCol(col); setSortDir(col === 'account' || col === 'name' ? 1 : -1); }
  };

  const openBoardPdf = () => {
    const win = window.open('', '_blank');
    if (win) win.location.href = `/api/erp-access/${selKey}/board-report.pdf?token=${localStorage.getItem('token')}`;
  };

  const sendEmail = async () => {
    setEmailSending(true); setEmailResult('');
    try {
      const r = await api.post(`/erp-access/${selKey}/email-report`, { to: emailTo });
      setEmailResult(r.data.message);
    } catch (e) { setEmailResult('✖ ' + (e.response?.data?.error || 'Failed to send')); }
    finally { setEmailSending(false); }
  };

  const selMeta = snapshots.find(s => s.key === selKey);
  const fmtSnapDate = (s) => s?.snapshot_date ? new Date(s.snapshot_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : s?.key;
  const asAtDate = selMeta?.snapshot_date
    ? new Date(selMeta.snapshot_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : selKey;
  const isDirector = user?.role === 'director';

  if (!['admin', 'director'].includes(user?.role)) {
    return <div className="p-8 text-center text-sm" style={{ color: '#64748b' }}>Access restricted.</div>;
  }

  const th = (label, col, align = 'left') => (
    <th onClick={() => setSort(col)} className="cursor-pointer select-none" style={{ padding: '8px 10px', textAlign: align, fontSize: 10, color: sortCol === col ? '#60a5fa' : '#334155', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
      {label}{sortCol === col ? (sortDir === -1 ? ' ↓' : ' ↑') : ''}
    </th>
  );

  return (
    <div className="space-y-5">
      <PageHeader title="ERP Access Review">
        {!isDirector && (
          <button onClick={() => { setMapModal(true); setMapTab('dates'); loadMappings(selKey); }} className="btn-ghost" title="Which company each ERP account belongs to">
            <Building2 size={13}/> Company Mapping
          </button>
        )}
        {!isDirector && (
          <button onClick={handleGenerate} disabled={generating} className="btn-primary">
            <RefreshCw size={13} className={generating ? 'animate-spin' : ''}/> {generating ? 'Analyzing PDFs…' : 'Generate'}
          </button>
        )}
        {selKey && (
          <>
            <button onClick={openBoardPdf} className="btn-ghost"><FileDown size={13}/> Board PDF</button>
            {!isDirector && <button onClick={() => { setEmailModal(true); setEmailResult(''); }} className="btn-ghost"><Mail size={13}/> Email Report</button>}
          </>
        )}
      </PageHeader>

      {error && <div className="p-3 rounded-xl text-xs" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171' }}>{error}</div>}

      {genResult && (
        <div className="p-3 rounded-xl text-xs" style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)', color: '#94a3b8' }}>
          {genResult.parsed.length ? <>Parsed: {genResult.parsed.map(p => `${p.key} (${p.users} users)`).join(', ')}. </> : null}
          {genResult.skipped.length ? <>Already stored: {genResult.skipped.join(', ')} — snapshots are kept permanently for history. </> : null}
          {genResult.failures?.length ? <span style={{ color: '#f87171' }}>Failed: {genResult.failures.map(f => `${f.key}: ${f.error}`).join('; ')}</span> : null}
        </div>
      )}

      {/* Snapshot controls — directors see a fixed "as at" banner, no selection */}
      {isDirector ? (
        selMeta && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl" style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)' }}>
            <ShieldCheck size={15} style={{ color: '#34d399' }}/>
            <span className="text-sm font-semibold" style={{ color: '#e2e8f0' }}>ERP user access as at {asAtDate}</span>
            <span className="text-xs ml-auto" style={{ color: '#475569' }}>{selMeta.user_count} user accounts reviewed · last updated {new Date(selMeta.updated_at || selMeta.created_at).toLocaleDateString('en-GB')}</span>
          </div>
        )
      ) : (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: '#475569' }}>Access snapshot</span>
            <select value={selKey} onChange={e => { setSelKey(e.target.value); if (e.target.value === baseKey) setBaseKey(''); }} className="input-dark text-xs py-1.5" style={{ width: 170 }}>
              {!snapshots.length && <option value="">— none generated —</option>}
              {snapshots.map(s => <option key={s.key} value={s.key}>{fmtSnapDate(s)} ({s.key})</option>)}
            </select>
          </div>
          {selMeta && <span className="text-xs" style={{ color: '#475569' }}>Access as at <strong style={{ color: '#34d399' }}>{asAtDate}</strong> · {selMeta.user_count} user exports · generated {new Date(selMeta.updated_at || selMeta.created_at).toLocaleDateString('en-GB')}</span>}
          {snapshots.length > 1 && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs" style={{ color: '#475569' }}>Compare with</span>
              <select value={baseKey} onChange={e => setBaseKey(e.target.value)} className="input-dark text-xs py-1.5" style={{ width: 170 }}>
                <option value="">— none —</option>
                {snapshots.filter(s => s.key !== selKey).map(s => <option key={s.key} value={s.key}>{fmtSnapDate(s)} ({s.key})</option>)}
              </select>
            </div>
          )}
        </div>
      )}

      {/* What this view is scoped to */}
      {data?.scope?.restricted && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs"
          style={{ background:'rgba(59,130,246,0.07)', border:'1px solid rgba(59,130,246,0.2)', color:'#94a3b8' }}>
          <Building2 size={13} style={{ color:'#60a5fa' }}/>
          Showing the <strong style={{ color:'#e2e8f0' }}>{data.scope.total}</strong> ERP accounts belonging to your
          {data.scope.companies?.length > 1 ? ' companies' : ' company'}.
          {data.scope.hidden > 0 && <span style={{ color:'#475569' }}> {data.scope.hidden} accounts in other companies are not shown.</span>}
        </div>
      )}
      {!isDirector && data?.scope && !data.scope.restricted && !data.scope.snapshot_mapped && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs"
          style={{ background:'rgba(245,158,11,0.07)', border:'1px solid rgba(245,158,11,0.25)', color:'#fbbf24' }}>
          <Building2 size={13}/>
          This ERP export is <strong>not mapped to a company</strong> — no director can see it at all.
          Map the upload date to the company whose ERP system it came from.
          <button onClick={() => { setMapModal(true); setMapTab('dates'); loadMappings(selKey); }} className="underline ml-1" style={{ color:'#fbbf24' }}>Map it now</button>
        </div>
      )}
      {!isDirector && data?.scope?.snapshot_mapped && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs"
          style={{ background:'rgba(52,211,153,0.06)', border:'1px solid rgba(52,211,153,0.2)', color:'#94a3b8' }}>
          <Building2 size={13} style={{ color:'#34d399' }}/>
          This export is mapped to <strong style={{ color:'#e2e8f0' }}>{data.scope.snapshot_company_name}</strong> — its
          directors can see all {data.scope.total} accounts.
          {data.scope.overrides > 0 && <span style={{ color:'#a78bfa' }}> {data.scope.overrides} account{data.scope.overrides === 1 ? ' is' : 's are'} pointed at a different company.</span>}
        </div>
      )}

      {!snapshots.length && !loading && (
        <Card>
          <div className="text-center py-10">
            <ShieldCheck size={36} style={{ color: '#334155', margin: '0 auto 12px' }}/>
            <div className="text-sm mb-1" style={{ color: '#94a3b8' }}>
              {isDirector ? 'No ERP access review for your company yet' : 'No access review available yet'}
            </div>
            {isDirector ? (
              <div className="text-xs" style={{ color: '#475569' }}>An access review appears here once IT has mapped an ERP export to your company.</div>
            ) : (
              <div className="text-xs" style={{ color: '#475569' }}>
                Drop the ERP per-user permission PDF exports into a <span className="font-mono" style={{ color: '#60a5fa' }}>ddmmyyyy</span> folder inside <span className="font-mono">ERP Logs</span>, then press <strong>Generate</strong>.
                {diskFolders.length ? <> Folders found on disk: <span className="font-mono" style={{ color: '#60a5fa' }}>{diskFolders.join(', ')}</span></> : ' No folders found on disk yet.'}
              </div>
            )}
          </div>
        </Card>
      )}

      {loading && <div className="flex items-center justify-center py-16 gap-2 text-xs" style={{ color: '#334155' }}><div className="w-4 h-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>Loading snapshot…</div>}

      {m && !loading && (
        <>
          {/* Global search — users, modules, permissions */}
          <div className="glass glow-border rounded-2xl p-4">
            <div className="relative">
              <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#475569' }}/>
              <input value={globalQ} onChange={e => setGlobalQ(e.target.value)}
                placeholder="Search anything — user account, employee name, module, or a specific permission (e.g. INDIKA, ACCOUNTS, JOB DELETE)…"
                className="input-dark w-full" style={{ paddingLeft: 36, paddingRight: 30 }}/>
              {globalQ && <button onClick={() => setGlobalQ('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#475569', cursor: 'pointer' }}><X size={13}/></button>}
            </div>
            {globalResults && (
              <div className="mt-3 space-y-3">
                {globalResults.empty && <div className="text-xs" style={{ color: '#475569' }}>No users, modules or permissions match "{globalQ}".</div>}
                {globalResults.users.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold mb-1.5" style={{ color: '#475569' }}>USERS — click to open the full user review</div>
                    <div className="flex flex-wrap gap-1.5">
                      {globalResults.users.map(u => (
                        <button key={u.id} onClick={() => { setUserModal(u); setShowDenied(false); }}
                          className="text-xs px-2.5 py-1.5 rounded-lg text-left"
                          style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)', cursor: 'pointer' }}>
                          <span className="font-mono" style={{ color: '#60a5fa' }}>{u.id}</span>
                          <span style={{ color: '#94a3b8' }}> · {u.name}</span>
                          <span style={{ color: '#475569' }}> · {u.total} grants · {u.coverage} modules</span>
                          {u.adminMods.length > 0 && <span style={{ color: '#f87171' }}> · ADMIN</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {globalResults.modules.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold mb-1.5" style={{ color: '#475569' }}>MODULES</div>
                    <div className="flex flex-wrap gap-1.5">
                      {globalResults.modules.map(mod => (
                        <button key={mod.name} onClick={() => setModuleModal(mod)}
                          className="text-xs px-2.5 py-1.5 rounded-lg"
                          style={{ background: 'rgba(129,140,248,0.08)', border: '1px solid rgba(129,140,248,0.25)', color: '#a5b4fc', cursor: 'pointer' }}>
                          {mod.name} <span style={{ color: '#475569' }}>· {mod.users} users · {mod.grants} grants</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {globalResults.perms.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold mb-1.5" style={{ color: '#475569' }}>PERMISSIONS — click to see who holds access in that module</div>
                    <div className="flex flex-wrap gap-1.5">
                      {globalResults.perms.map((p, i) => (
                        <button key={i} onClick={() => setModuleModal(p.modStats)}
                          className="text-xs px-2.5 py-1.5 rounded-lg"
                          style={{ background: 'rgba(52,211,153,0.07)', border: '1px solid rgba(52,211,153,0.22)', cursor: 'pointer' }}>
                          <span style={{ color: '#34d399' }}>{p.label}</span>
                          <span style={{ color: '#475569' }}> · {p.module} · {p.holders} user{p.holders !== 1 ? 's' : ''} hold this</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              ['Users reviewed', m.users.length, '', '#60a5fa'],
              ['Modules in ERP', data.modules.length, `${m.catalog.toLocaleString()} permissions in catalogue`, '#a78bfa'],
              ['Permissions granted', m.granted.toLocaleString(), `${m.pct.toFixed(1)}% of possible`, '#34d399'],
              ['Average per user', m.avg, 'granted permissions', '#2dd4bf'],
              ['Admin authority', m.adminUsers.length, 'users with ADMIN rights', '#f87171'],
              ['Zero-access accounts', m.zeroUsers.length, m.zeroUsers.length ? 'review for removal' : 'none found', m.zeroUsers.length ? '#fbbf24' : '#34d399'],
            ].map(([label, val, sub, color]) => (
              <div key={label} className="glass glow-border rounded-2xl p-4" style={{ borderTop: `2px solid ${color}55` }}>
                <div className="text-xs font-medium mb-1" style={{ color: '#475569' }}>{label}</div>
                <div className="text-3xl font-bold" style={{ color }}>{val}</div>
                {sub && <div className="text-xs mt-0.5" style={{ color: '#334155' }}>{sub}</div>}
              </div>
            ))}
          </div>

          {/* Comparison */}
          {compare && (
            <Card title={`Changes vs ${baseKey}`}>
              <div className="text-xs mb-3" style={{ color: '#94a3b8' }}>
                {compare.added.length} new account{compare.added.length !== 1 ? 's' : ''}{compare.added.length ? ` (${compare.added.join(', ')})` : ''} ·{' '}
                {compare.removed.length} removed{compare.removed.length ? ` (${compare.removed.join(', ')})` : ''} ·{' '}
                {compare.changes.length} account{compare.changes.length !== 1 ? 's' : ''} with permission changes
              </div>
              {compare.changes.length > 0 && (
                <div className="overflow-x-auto">
                  <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                    <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                      {['Account', 'Gained', 'Lost', 'Affected modules'].map(h => <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontSize: 10, color: '#334155', fontWeight: 700, textTransform: 'uppercase' }}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {compare.changes.slice(0, 40).map(c => (
                        <tr key={c.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td style={{ padding: '6px 10px', fontFamily: 'monospace', color: '#60a5fa' }}>{c.id}</td>
                          <td style={{ padding: '6px 10px', color: '#34d399' }}>{c.gained ? `+${c.gained}` : '—'}</td>
                          <td style={{ padding: '6px 10px', color: '#f87171' }}>{c.lost ? `−${c.lost}` : '—'}</td>
                          <td style={{ padding: '6px 10px', color: '#94a3b8' }}>{c.mods.slice(0, 5).join(', ')}{c.mods.length > 5 ? ` +${c.mods.length - 5}` : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card title="Module-wise access" right={
              <select value={moduleMetric} onChange={e => setModuleMetric(e.target.value)} className="input-dark text-xs py-1" style={{ width: 170 }}>
                <option value="users">Users with access</option>
                <option value="grants">Permissions granted</option>
              </select>
            }>
              <HBar
                color="#3b82f6"
                items={m.modules.slice().sort((a, b) => (moduleMetric === 'users' ? b.users - a.users : b.grants - a.grants))
                  .map(mod => ({ label: mod.name, value: moduleMetric === 'users' ? mod.users : mod.grants, tooltip: `${mod.name} — ${mod.users} users · ${mod.grants} grants · ${mod.size} permissions in module`, mod }))}
                onClick={it => setModuleModal(it.mod)}
              />
            </Card>
            <div className="space-y-5">
              <Card title="Access-type mix">
                <HBar color="#818cf8"
                  items={data.typeOrder.map((t, i) => ({ label: t, value: m.byTypeAll[i], tooltip: `${t} — ${(m.byTypeAll[i] / m.granted * 100).toFixed(1)}% of all grants` })).filter(i => i.value > 0)}/>
              </Card>
              <Card title="Top users by granted permissions">
                <HBar color="#34d399"
                  items={m.users.slice().sort((a, b) => b.total - a.total).slice(0, 15)
                    .map(u => ({ label: u.id, value: u.total, tooltip: u.name, u }))}
                  onClick={it => { setUserModal(it.u); setShowDenied(false); }}/>
              </Card>
            </div>
          </div>

          {/* Risk cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {RISK_DEFS.map(rd => {
              const list = m[rd.key];
              return (
                <div key={rd.key} className="glass glow-border rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span style={{ width: 8, height: 8, borderRadius: 99, background: rd.color, display: 'inline-block' }}/>
                    <span className="text-xs font-semibold" style={{ color: '#e2e8f0' }}>{rd.title}</span>
                    <span className="ml-auto text-xl font-bold" style={{ color: rd.color }}>{list.length}</span>
                  </div>
                  <div className="text-xs mb-2" style={{ color: '#475569' }}>{rd.def}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {list.slice(0, 8).map(u => (
                      <button key={u.id} onClick={() => { setUserModal(u); setShowDenied(false); }}
                        className="text-xs px-2 py-0.5 rounded-full font-mono"
                        style={{ background: `${rd.color}18`, color: rd.color, border: `1px solid ${rd.color}30`, cursor: 'pointer' }}>
                        {rd.fmt(u)}
                      </button>
                    ))}
                    {list.length > 8 && <span className="text-xs px-1 py-0.5" style={{ color: '#475569' }}>+{list.length - 8} more</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* User explorer */}
          <Card title={`User explorer (${filteredUsers.length}${riskFilter || search ? ` of ${m.users.length}` : ''})`} right={
            <div className="relative">
              <Search size={12} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#475569' }}/>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search account or employee…"
                className="input-dark text-xs py-1.5" style={{ paddingLeft: 28, width: 230 }}/>
              {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#475569', cursor: 'pointer' }}><X size={11}/></button>}
            </div>
          }>
            {/* Quick risk filters */}
            <div className="flex flex-wrap gap-1.5 mb-3">
              {[
                ['', `All ${m.users.length}`, '#60a5fa'],
                ['admin', `ADMIN rights ${m.adminUsers.length}`, '#f87171'],
                ['sod', `SoD conflicts ${m.sodUsers.length}`, '#fb923c'],
                ['delete', `Delete rights ${m.deleteUsers.length}`, '#fbbf24'],
                ['approve', `Approve rights ${m.users.filter(u => u.byType[4] > 0).length}`, '#c084fc'],
                ['zero', `Zero access ${m.zeroUsers.length}`, '#34d399'],
              ].map(([key, label, color]) => (
                <button key={key} onClick={() => setRiskFilter(riskFilter === key ? '' : key)}
                  className="text-xs px-2.5 py-1 rounded-full"
                  style={{
                    background: riskFilter === key ? `${color}25` : 'rgba(255,255,255,0.04)',
                    color: riskFilter === key ? color : '#94a3b8',
                    border: `1px solid ${riskFilter === key ? `${color}55` : 'rgba(255,255,255,0.08)'}`,
                    cursor: 'pointer', fontWeight: riskFilter === key ? 600 : 400,
                  }}>
                  {label}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto" style={{ maxHeight: 480, overflowY: 'auto' }}>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#0b1322', zIndex: 1 }}><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  {th('Account', 'account')}
                  {th('Employee', 'name')}
                  {th('Granted', 'total', 'right')}
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, color: '#334155', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', width: 120 }}>Access share</th>
                  {th('Modules', 'coverage', 'right')}
                  {th('Delete', 'del', 'right')}
                  {th('Approve', 'appr', 'right')}
                  {th('Admin', 'admin', 'right')}
                  {th('SoD', 'sod', 'right')}
                </tr></thead>
                <tbody>
                  {filteredUsers.map(u => {
                    const maxTotal = maxUserTotal;
                    return (
                    <tr key={u.id} onClick={() => { setUserModal(u); setShowDenied(false); }} className="cursor-pointer"
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.05)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                        <span style={{ fontFamily: 'monospace', color: '#60a5fa' }}>{u.id}</span>
                        {u.adminMods.length > 0 && <span className="ml-1.5 px-1 rounded" style={{ fontSize: 8.5, background: 'rgba(239,68,68,0.15)', color: '#f87171', fontWeight: 700 }}>ADMIN</span>}
                        {u.sodMods.length > 0 && <span className="ml-1 px-1 rounded" style={{ fontSize: 8.5, background: 'rgba(251,146,60,0.15)', color: '#fb923c', fontWeight: 700 }}>SoD</span>}
                        {u.total === 0 && <span className="ml-1 px-1 rounded" style={{ fontSize: 8.5, background: 'rgba(52,211,153,0.15)', color: '#34d399', fontWeight: 700 }}>ZERO</span>}
                      </td>
                      <td style={{ padding: '6px 10px', color: '#e2e8f0', whiteSpace: 'nowrap' }}>{u.name}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#e2e8f0', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{u.total}</td>
                      <td style={{ padding: '6px 10px' }}>
                        <div className="flex items-center gap-1.5">
                          <div className="rounded-full" style={{ flex: 1, height: 5, background: 'rgba(255,255,255,0.05)' }}>
                            <div className="rounded-full" style={{ width: `${u.total / maxTotal * 100}%`, height: 5, background: u.adminMods.length ? '#f87171' : '#3b82f6' }}/>
                          </div>
                          <span style={{ fontSize: 9.5, color: '#475569', width: 34, textAlign: 'right' }}>{(u.total / m.catalog * 100).toFixed(1)}%</span>
                        </div>
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{u.coverage}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: u.delCount ? '#fbbf24' : '#334155', fontVariantNumeric: 'tabular-nums' }}>{u.delCount || '—'}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: u.byType[4] ? '#a78bfa' : '#334155', fontVariantNumeric: 'tabular-nums' }}>{u.byType[4] || '—'}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: u.adminMods.length ? '#f87171' : '#334155', fontVariantNumeric: 'tabular-nums' }}>{u.adminMods.length || '—'}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: u.sodMods.length ? '#fb923c' : '#334155', fontVariantNumeric: 'tabular-nums' }}>{u.sodMods.length || '—'}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="text-xs" style={{ color: '#334155' }}>Source: ERP per-user permission exports · snapshot folder {data.key}{data.users[0]?.printed ? ` · printed ${data.users[0].printed}` : ''}</div>
        </>
      )}

      {/* ── Module modal ── */}
      {moduleModal && (
        <Modal title={`${moduleModal.name} — ${moduleModal.users} users · ${moduleModal.grants} grants`} onClose={() => setModuleModal(null)} size="lg">
          <div className="flex flex-wrap gap-1.5">
            {moduleModal.holders.slice().sort((a, b) => b.granted - a.granted).map(h => (
              <button key={h.id} onClick={() => {
                const u = m.users.find(x => x.id === h.id);
                if (u) { setModuleModal(null); setUserModal(u); setShowDenied(false); }
              }} className="text-xs px-2 py-1 rounded-lg font-mono"
                style={{ background: 'rgba(59,130,246,0.1)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.2)', cursor: 'pointer' }}>
                {h.id} <span style={{ color: '#475569' }}>{h.granted}/{moduleModal.size}</span>
              </button>
            ))}
            {!moduleModal.holders.length && <div className="text-xs py-4 text-center w-full" style={{ color: '#334155' }}>No users hold access in this module</div>}
          </div>
        </Modal>
      )}

      {/* ── User review modal — full per-user insight ── */}
      {userModal && data && (
        <Modal title={`User Review — ${userModal.name}`} onClose={() => setUserModal(null)} size="xl">
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="flex items-center gap-3 flex-wrap text-xs">
              <span className="font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(59,130,246,0.1)', color: '#60a5fa' }}>{userModal.id}</span>
              {userModal.printed && <span style={{ color: '#475569' }}>ERP export printed {userModal.printed}</span>}
              <button onClick={() => setShowDenied(v => !v)} className="btn-ghost text-xs px-2 py-1 ml-auto">
                {showDenied ? <ChevronUp size={11}/> : <ChevronDown size={11}/>} {showDenied ? 'Granted only' : 'Show not-granted too'}
              </button>
            </div>

            {/* KPI chips */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {[
                ['Granted', userModal.total, '#60a5fa', `of ${m.catalog.toLocaleString()} (${(userModal.total / m.catalog * 100).toFixed(1)}%)`],
                ['Modules', `${userModal.coverage}/${data.modules.length}`, '#a78bfa', 'with access'],
                ['Delete rights', userModal.delCount, userModal.delCount ? '#fbbf24' : '#334155', ''],
                ['Approve rights', userModal.byType[4], userModal.byType[4] ? '#c084fc' : '#334155', ''],
                ['Admin modules', userModal.adminMods.length, userModal.adminMods.length ? '#f87171' : '#334155', ''],
                ['SoD conflicts', userModal.sodMods.length, userModal.sodMods.length ? '#fb923c' : '#334155', ''],
              ].map(([label, val, color, sub]) => (
                <div key={label} className="rounded-xl p-2.5 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="text-lg font-bold" style={{ color }}>{val}</div>
                  <div className="text-xs" style={{ color: '#475569' }}>{label}</div>
                  {sub && <div style={{ color: '#334155', fontSize: 9.5 }}>{sub}</div>}
                </div>
              ))}
            </div>

            {/* vs org average */}
            <div className="text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.15)', color: '#94a3b8' }}>
              {userModal.total === 0
                ? 'This account holds no ERP permissions — candidate for removal.'
                : <>Holds <strong style={{ color: '#e2e8f0' }}>{userModal.total}</strong> permissions vs an organisation average of <strong style={{ color: '#e2e8f0' }}>{m.avg}</strong>
                  {' '}({userModal.total >= m.avg ? <span style={{ color: '#fbbf24' }}>{(userModal.total / m.avg).toFixed(1)}× above average</span> : <span style={{ color: '#34d399' }}>below average</span>})
                  {m.users.length > 1 && <> · ranks <strong style={{ color: '#e2e8f0' }}>#{m.users.filter(x => x.total > userModal.total).length + 1}</strong> of {m.users.length} users by access</>}
                </>}
            </div>

            {(userModal.adminMods.length > 0 || userModal.sodMods.length > 0) && (
              <div className="p-2.5 rounded-lg text-xs" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171' }}>
                {userModal.adminMods.length > 0 && <div>⚠ ADMIN rights in: {userModal.adminMods.join(', ')}</div>}
                {userModal.sodMods.length > 0 && <div>⚠ Maker-checker conflict (can create/edit AND approve) in: {userModal.sodMods.join(', ')}</div>}
              </div>
            )}

            {/* Access profile charts */}
            {userModal.total > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-semibold mb-2" style={{ color: '#94a3b8' }}>Access-type profile</div>
                  <HBar color="#818cf8" items={data.typeOrder.map((t, i) => ({ label: t, value: userModal.byType[i] })).filter(i => i.value > 0)}/>
                </div>
                <div>
                  <div className="text-xs font-semibold mb-2" style={{ color: '#94a3b8' }}>Top modules by grants</div>
                  <HBar color="#3b82f6" items={data.modules.map((mod, mi) => ({ label: mod.name, value: userModal.perMod[mi] })).filter(i => i.value > 0).sort((a, b) => b.value - a.value).slice(0, 8)}/>
                </div>
              </div>
            )}

            <div className="text-xs font-semibold pt-1" style={{ color: '#94a3b8', borderTop: '1px solid rgba(255,255,255,0.06)' }}>Permission detail — module by module</div>
            {data.modules.map((mod, mi) => {
              const bits = userModal.grantedBits[mi];
              if (!bits.size && !showDenied) return null;
              return (
                <div key={mod.name}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold" style={{ color: '#e2e8f0' }}>{mod.name}</span>
                    <span className="text-xs" style={{ color: '#475569' }}>{bits.size}/{mod.labels.length}</span>
                    <div className="flex-1 h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <div className="h-1 rounded-full" style={{ width: `${bits.size / mod.labels.length * 100}%`, background: '#3b82f6' }}/>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3" style={{ rowGap: 2 }}>
                    {mod.labels.map((label, i) => {
                      const g = bits.has(i);
                      if (!g && !showDenied) return null;
                      return (
                        <div key={i} className="flex items-center gap-1.5 text-xs" style={{ color: g ? '#94a3b8' : '#334155' }}>
                          <span style={{ width: 5, height: 5, borderRadius: 99, flexShrink: 0, background: g ? '#3b82f6' : '#334155' }}/>
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </Modal>
      )}

      {/* ── Email modal ── */}
      {emailModal && (
        <Modal title="Email Board Report" onClose={() => setEmailModal(false)} size="md">
          <div className="space-y-4">
            <div className="text-xs" style={{ color: '#94a3b8' }}>
              Sends the system-generated PDF report for snapshot <span className="font-mono" style={{ color: '#60a5fa' }}>{selKey}</span> with a KPI summary in the email body.
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>Recipient email <span style={{ color: '#334155' }}>(blank = IT dept)</span></label>
              <input value={emailTo} onChange={e => setEmailTo(e.target.value)} className="input-dark" placeholder="e.g. duminda@clsynergy.com"/>
            </div>
            {emailResult && <div className="text-xs" style={{ color: emailResult.startsWith('✖') ? '#f87171' : '#34d399' }}>{emailResult}</div>}
            <div className="flex gap-3">
              <button onClick={() => setEmailModal(false)} className="btn-ghost flex-1 justify-center">Close</button>
              <button onClick={sendEmail} disabled={emailSending} className="btn-primary flex-1 justify-center">
                {emailSending ? 'Sending…' : 'Send Report'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Company mapping: by upload date, and by user ─────────────── */}
      {mapModal && (
        <Modal title="ERP Access — Company Mapping" onClose={() => setMapModal(false)} size="lg">
          <div className="space-y-3">
            <div className="text-xs" style={{ color: '#94a3b8' }}>
              An ERP export comes out of one company's ERP system, so the quickest way is to map the
              whole upload date. Individual accounts can then be pointed elsewhere if they belong to a
              different company. <strong style={{ color: '#cbd5e1' }}>Until an upload is mapped, no director sees it at all.</strong>
            </div>

            <div className="flex gap-1.5">
              {[['dates', 'By upload date'], ['users', 'By user account']].map(([k, label]) => (
                <button key={k} onClick={() => setMapTab(k)}
                  style={{ fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 7, cursor: 'pointer',
                    background: mapTab === k ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${mapTab === k ? 'rgba(59,130,246,0.4)' : 'rgba(255,255,255,0.08)'}`,
                    color: mapTab === k ? '#60a5fa' : '#94a3b8' }}>{label}</button>
              ))}
            </div>

            {mapTab === 'dates' ? (
              <>
                <div className="text-xs" style={{ color: '#475569' }}>
                  Each ERP export and the company it came from. Every account in that export inherits it.
                </div>
                <div style={{ maxHeight: '52vh', overflowY: 'auto' }}>
                  {mapLoading ? <div className="py-8 text-center text-xs" style={{ color: '#334155' }}>Loading…</div>
                   : mapSnapshots.length === 0 ? <div className="py-8 text-center text-xs" style={{ color: '#334155' }}>No ERP exports stored yet</div>
                   : mapSnapshots.map(sn => (
                    <div key={sn.key} className="flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1.5"
                      style={{ background: sn.company_id ? 'rgba(52,211,153,0.06)' : 'rgba(245,158,11,0.07)',
                               border: `1px solid ${sn.company_id ? 'rgba(52,211,153,0.2)' : 'rgba(245,158,11,0.25)'}` }}>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold" style={{ color: '#e2e8f0' }}>
                          {sn.snapshot_date ? new Date(sn.snapshot_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : sn.key}
                        </div>
                        <div className="text-xs" style={{ color: '#475569' }}>
                          {sn.user_count} accounts · upload {sn.key}
                          {!sn.company_id && <span style={{ color: '#fbbf24' }}> · not mapped — hidden from all directors</span>}
                        </div>
                      </div>
                      <select value={sn.company_id || ''} onChange={e => setSnapshotCompany(sn.key, e.target.value)}
                        className="input-dark text-xs py-1.5" style={{ width: 210 }}>
                        <option value="">— not mapped —</option>
                        {mapCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <select value={mapKey} onChange={e => loadMappings(e.target.value)} className="input-dark text-xs py-1.5" style={{ width: 190 }}>
                    {mapSnapshots.map(sn => <option key={sn.key} value={sn.key}>
                      {sn.snapshot_date ? new Date(sn.snapshot_date).toLocaleDateString('en-GB') : sn.key} ({sn.key})
                    </option>)}
                  </select>
                  <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg flex-1" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', minWidth: 180 }}>
                    <Search size={11} style={{ color: '#475569' }}/>
                    <input value={mapSearch} onChange={e => setMapSearch(e.target.value)} placeholder="Search account or name..."
                      className="bg-transparent text-xs outline-none w-full" style={{ color: '#e2e8f0' }}/>
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: '#94a3b8' }}>
                    <input type="checkbox" checked={mapOnlyUnmapped} onChange={e => setMapOnlyUnmapped(e.target.checked)}/>
                    Unmapped only
                  </label>
                  <button onClick={runAutoMatch} disabled={matching} className="btn-ghost text-xs" style={{ color: '#a78bfa' }}
                    title="Match accounts to employees by name and set an override where the employee is in a different company">
                    <Wand2 size={12}/> {matching ? 'Matching…' : 'Auto-match by name'}
                  </button>
                </div>

                {(() => {
                  const q = mapSearch.trim().toLowerCase();
                  const rows = mapRows.filter(r =>
                    (!mapOnlyUnmapped || !r.effective_company_id) &&
                    (!q || r.account_id.toLowerCase().includes(q) || String(r.account_name || '').toLowerCase().includes(q)));
                  const unmapped = mapRows.filter(r => !r.effective_company_id).length;
                  const overrides = mapRows.filter(r => r.override_company_id).length;
                  return (
                    <>
                      <div className="text-xs" style={{ color: '#475569' }}>
                        {mapRows.length} accounts · <strong style={{ color: unmapped ? '#fbbf24' : '#34d399' }}>{unmapped}</strong> with no company
                        {overrides > 0 && <> · {overrides} pointed at a different company</>}
                        {rows.length !== mapRows.length && <> · showing {rows.length}</>}
                      </div>
                      <div style={{ maxHeight: '46vh', overflowY: 'auto' }}>
                        {mapLoading ? <div className="py-8 text-center text-xs" style={{ color: '#334155' }}>Loading…</div>
                         : rows.length === 0 ? <div className="py-8 text-center text-xs" style={{ color: '#334155' }}>Nothing to show</div>
                         : rows.map(r => (
                          <div key={r.account_id} className="flex items-center gap-3 px-3 py-2 rounded-lg mb-1.5"
                            style={{ background: r.effective_company_id ? 'rgba(255,255,255,0.03)' : 'rgba(245,158,11,0.06)',
                                     border: `1px solid ${r.effective_company_id ? 'rgba(255,255,255,0.06)' : 'rgba(245,158,11,0.2)'}` }}>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-mono font-semibold" style={{ color: '#e2e8f0' }}>{r.account_id}</div>
                              <div className="text-xs truncate" style={{ color: '#475569' }}>
                                {r.account_name || '—'}
                                {r.matched_by === 'snapshot' && <span style={{ color: '#334155' }}> · from the upload date</span>}
                                {r.override_company_id && <span style={{ color: '#a78bfa' }}> · set for this account</span>}
                              </div>
                            </div>
                            <select value={r.override_company_id || ''} onChange={e => setAccountCompany(r.account_id, e.target.value)}
                              className="input-dark text-xs py-1.5" style={{ width: 210 }}>
                              <option value="">
                                {r.effective_company_name && !r.override_company_id
                                  ? `Same as upload — ${r.effective_company_name}` : '— use the upload date —'}
                              </option>
                              {mapCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </>
            )}

            <div className="flex gap-3">
              <button onClick={() => { setMapModal(false); loadSnapshots(false); if (selKey) api.get(`/erp-access/snapshots/${selKey}`).then(r => setData(r.data)).catch(() => {}); }}
                className="btn-primary flex-1 justify-center">Done</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
